import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, unref as _unref } from "vue"

const _hoisted_1 = {
  class: "d-flex align-center justify-space-between flex-wrap gap-2 px-4 py-3 bg-green-lighten-5 border-b border-green-lighten-3",
  "data-cy": "statement-ready-alert"
}
const _hoisted_2 = { class: "d-flex align-center gap-2 flex-wrap" }
const _hoisted_3 = { class: "d-inline-flex align-center gap-1 font-weight-bold bg-green-lighten-5 text-green-darken-3 px-3 py-1 rounded-pill text-body-2" }
const _hoisted_4 = { class: "text-grey-darken-1 text-body-2" }
const _hoisted_5 = { class: "d-flex align-center gap-2 flex-wrap" }
const _hoisted_6 = ["href"]
const _hoisted_7 = { class: "text-center whitespace-pre-line" }
const _hoisted_8 = { class: "d-flex align-center gap-3 px-4 pt-3 pb-2 border-b border-grey-lighten-3 text-grey-darken-1 text-body-2 font-weight-medium" }
const _hoisted_9 = {
  key: 0,
  class: "text-caption font-weight-medium text-blue-darken-2 bg-blue-lighten-5 px-2 py-[2px] rounded-pill"
}
const _hoisted_10 = { class: "d-flex align-center gap-4 px-4 py-2 flex-wrap bg-grey-lighten-5 border-t border-grey-lighten-3" }
const _hoisted_11 = ["href", "aria-disabled"]
const _hoisted_12 = ["aria-disabled"]
const _hoisted_13 = {
  class: "flex flex-col items-center py-8 gap-4",
  "data-cy": "polling-state"
}
const _hoisted_14 = {
  key: 1,
  class: "flex flex-col items-center py-8 gap-4",
  "data-cy": "preview-loading-state"
}
const _hoisted_15 = {
  key: 3,
  class: "w-full border rounded mb-4 overflow-auto max-h-[600px]",
  "data-cy": "statement-preview-pre"
}
const _hoisted_16 = { class: "flex gap-3 mt-2" }

import { computed } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import AlertBar from '@/components/AlertBar.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlIcon from '@/components/LlIcon.vue';
import SettlementPostActions from './SettlementPostActions.vue';
import SettlementCompletePanel from './SettlementCompletePanel.vue';
import { formatLongDateString } from '@/services/utils';
import { VuePdfEmbed } from '@/utils/pdfHelpers';


export default /*@__PURE__*/_defineComponent({
  __name: 'SettlementStatementStepV2',
  props: {
  /** Number shown in the title — the parent renumbers when Map Payments is skipped. */
  stepNumber: { type: Number, default: 6 },
  stageUpdated: { type: Boolean, default: false },
  // ── Shared with V1 ────────────────────────────────────
  statementReady: { type: Boolean, default: false },
  polling: { type: Boolean, default: false },
  timedOut: { type: Boolean, default: false },
  generating: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  downloadUrl: { type: String, default: null },
  generatedDate: { type: String, default: null },
  canGenerate: { type: Boolean, default: true },
  previewUrl: { type: String, default: null },
  previewGenerating: { type: Boolean, default: false },
  previewPolling: { type: Boolean, default: false },
  previewTimedOut: { type: Boolean, default: false },
  canReset: { type: Boolean, default: true },
  resetBlockedReason: { type: String, default: null },
  /** Parent-provided feature flag for the post-settlement actions panel. */
  showPostSettlementActions: { type: Boolean, required: true },
  /**
   * When true (settlementRedesign path), render the redesigned disbursement
   * table instead of the 3-column post-settlement actions. The table is
   * store-connected, so it needs none of the post-settlement props below.
   */
  useDisbursementTable: { type: Boolean, default: false },

  // ── Post-Settlement Actions ────────────────────────────
  // NOTE: the props below are consumed ONLY by the legacy 3-column
  // SettlementPostActions (rendered when `useDisbursementTable` is false — i.e.
  // the settlementRedesign fallback). The redesign path's SettlementCompletePanel
  // is store-connected and ignores them. They intentionally stay for the fallback;
  // remove this block when the settlementRedesign flag is retired.
  invoiceFinalized: { type: Boolean, default: false },
  canFinalizeInvoice: { type: Boolean, default: false },
  canUndoInvoice: { type: Boolean, default: false },
  invoiceBlockedReason: { type: String, default: null },
  trustPaid: { type: Boolean, default: false },
  canPayFromTrust: { type: Boolean, default: false },
  canUndoTrustPayment: { type: Boolean, default: false },
  trustPaymentBlockedReason: { type: String, default: null },
  undoTrustPaymentBlockedReason: { type: String, default: null },
  qboPushed: { type: Boolean, default: false },
  canPushClientDisbursement: { type: Boolean, default: false },
  canUndoClientDisbursement: { type: Boolean, default: false },
  disbursementBlockedReason: { type: String, default: null },
  undoDisbursementBlockedReason: { type: String, default: null },
  invoiceAmount: { type: Number, default: null },
  netToClient: { type: Number, default: null },
  trustBalance: { type: Number, default: null },
  trustBalanceLoading: { type: Boolean, default: false },
  trustBalanceError: { type: Boolean, default: false },
  /** SC-46858 flipper — forwarded to SettlementPostActions for its trust tooltip copy. */
  clientAccountingEnabled: { type: Boolean, default: false },
  trustInsufficient: { type: Boolean, default: false },
  postSaving: { type: Boolean, default: false },
  postError: { type: String, default: null },
  postColumnError: { type: String, default: null },
  savingColumn: { type: String, default: null },
  trustStatementLoading: { type: Boolean, default: false },
},
  emits: [
  'retry-polling',
  'unlock-and-reset',
  'download',
  'go-back',
  'generate',
  'retry-preview',
  'download-trust-statement',
  // Post-settlement
  'finalize-invoice',
  'undo-invoice',
  'pay-trust',
  'undo-trust',
  'push-qbo',
  'undo-qbo',
  'view-invoice',
],
  setup(__props) {

const props = __props;



const generatedDateFormatted = computed(() => {
  if (!props.generatedDate) return '';
  return formatLongDateString(props.generatedDate) || props.generatedDate;
});

const stageBadge = computed(() =>
  props.stageUpdated ? 'Settlement Pending' : null,
);

return (_ctx: any,_cache: any) => {
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(_component_v_card, {
    flat: "",
    class: "border-0 !bg-[rgb(var(--v-theme-surface))] overflow-hidden"
  }, {
    default: _withCtx(() => [
      (__props.statementReady)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            (__props.useDisbursementTable)
              ? (_openBlock(), _createBlock(_component_v_card_text, {
                  key: 0,
                  class: "pa-4"
                }, {
                  default: _withCtx(() => [
                    _createVNode(SettlementCompletePanel, {
                      "generated-date": __props.generatedDate,
                      "download-url": __props.downloadUrl,
                      "can-reset": __props.canReset,
                      "reset-blocked-reason": __props.resetBlockedReason,
                      "invoice-finalized": __props.invoiceFinalized,
                      "trust-statement-loading": __props.trustStatementLoading,
                      onUnlockAndReset: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('unlock-and-reset'))),
                      onDownload: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('download'))),
                      onDownloadTrustStatement: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('download-trust-statement'))),
                      onViewInvoice: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('view-invoice')))
                    }, null, 8, ["generated-date", "download-url", "can-reset", "reset-blocked-reason", "invoice-finalized", "trust-statement-loading"])
                  ]),
                  _: 1
                }))
              : (_openBlock(), _createBlock(_component_v_card_text, {
                  key: 1,
                  class: "pa-0"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                      _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("span", _hoisted_3, [
                          _createVNode(LlIcon, {
                            icon: "mdi-check",
                            color: "success",
                            size: "14"
                          }),
                          _cache[19] || (_cache[19] = _createTextVNode(" Settlement Complete ", -1))
                        ]),
                        _createElementVNode("span", _hoisted_4, " Generated " + _toDisplayString(generatedDateFormatted.value), 1)
                      ]),
                      _createElementVNode("div", _hoisted_5, [
                        (__props.downloadUrl)
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 0,
                              href: __props.downloadUrl,
                              download: "settlement-statement.pdf",
                              class: "text-decoration-none",
                              "data-cy": "download-pdf-link",
                              onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('download')))
                            }, [
                              _createVNode(LlSecondaryButton, {
                                "prepend-icon": "mdi-download",
                                size: "small"
                              }, {
                                default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                  _createTextVNode(" Download PDF ", -1)
                                ]))]),
                                _: 1
                              })
                            ], 8, _hoisted_6))
                          : _createCommentVNode("", true),
                        (!__props.canReset)
                          ? (_openBlock(), _createBlock(LlTooltip, {
                              key: 1,
                              location: "top"
                            }, {
                              activator: _withCtx(() => [
                                _createVNode(LlSecondaryButton, {
                                  color: "error",
                                  variant: "text",
                                  "prepend-icon": "mdi-lock",
                                  size: "small",
                                  "data-cy": "unlock-reset-button",
                                  disabled: ""
                                }, {
                                  default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                    _createTextVNode(" Unlock & Reset ", -1)
                                  ]))]),
                                  _: 1
                                })
                              ]),
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_7, _toDisplayString(__props.resetBlockedReason || 'Reset is not available'), 1)
                              ]),
                              _: 1
                            }))
                          : (_openBlock(), _createBlock(LlSecondaryButton, {
                              key: 2,
                              color: "error",
                              variant: "text",
                              "prepend-icon": "mdi-lock-open",
                              size: "small",
                              "data-cy": "unlock-reset-button",
                              onClick: _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('unlock-and-reset')))
                            }, {
                              default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                                _createTextVNode(" Unlock & Reset ", -1)
                              ]))]),
                              _: 1
                            }))
                      ])
                    ]),
                    (__props.showPostSettlementActions)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createElementVNode("div", _hoisted_8, [
                            _cache[23] || (_cache[23] = _createTextVNode(" Post-Settlement Actions ", -1)),
                            (stageBadge.value)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_9, " ✓ Stage → " + _toDisplayString(stageBadge.value), 1))
                              : _createCommentVNode("", true)
                          ]),
                          _createVNode(SettlementPostActions, {
                            "invoice-finalized": __props.invoiceFinalized,
                            "can-finalize-invoice": __props.canFinalizeInvoice,
                            "can-undo-invoice": __props.canUndoInvoice,
                            "invoice-blocked-reason": __props.invoiceBlockedReason,
                            "trust-paid": __props.trustPaid,
                            "can-pay-from-trust": __props.canPayFromTrust,
                            "can-undo-trust-payment": __props.canUndoTrustPayment,
                            "trust-payment-blocked-reason": __props.trustPaymentBlockedReason,
                            "undo-trust-payment-blocked-reason": __props.undoTrustPaymentBlockedReason,
                            "qbo-pushed": __props.qboPushed,
                            "can-push-client-disbursement": __props.canPushClientDisbursement,
                            "can-undo-client-disbursement": __props.canUndoClientDisbursement,
                            "disbursement-blocked-reason": __props.disbursementBlockedReason,
                            "undo-disbursement-blocked-reason": __props.undoDisbursementBlockedReason,
                            "invoice-amount": __props.invoiceAmount,
                            "net-to-client": __props.netToClient,
                            "trust-balance": __props.trustBalance,
                            "trust-balance-loading": __props.trustBalanceLoading,
                            "trust-balance-error": __props.trustBalanceError,
                            "client-accounting-enabled": __props.clientAccountingEnabled,
                            "trust-insufficient": __props.trustInsufficient,
                            saving: __props.postSaving,
                            "saving-column": __props.savingColumn,
                            error: __props.postError,
                            "column-error": __props.postColumnError,
                            "stage-badge": stageBadge.value,
                            onFinalizeInvoice: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$emit('finalize-invoice'))),
                            onUndoInvoice: _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('undo-invoice'))),
                            onPayTrust: _cache[8] || (_cache[8] = ($event: any) => (_ctx.$emit('pay-trust'))),
                            onUndoTrust: _cache[9] || (_cache[9] = ($event: any) => (_ctx.$emit('undo-trust'))),
                            onPushQbo: _cache[10] || (_cache[10] = ($event: any) => (_ctx.$emit('push-qbo'))),
                            onUndoQbo: _cache[11] || (_cache[11] = ($event: any) => (_ctx.$emit('undo-qbo'))),
                            onViewInvoice: _cache[12] || (_cache[12] = ($event: any) => (_ctx.$emit('view-invoice')))
                          }, null, 8, ["invoice-finalized", "can-finalize-invoice", "can-undo-invoice", "invoice-blocked-reason", "trust-paid", "can-pay-from-trust", "can-undo-trust-payment", "trust-payment-blocked-reason", "undo-trust-payment-blocked-reason", "qbo-pushed", "can-push-client-disbursement", "can-undo-client-disbursement", "disbursement-blocked-reason", "undo-disbursement-blocked-reason", "invoice-amount", "net-to-client", "trust-balance", "trust-balance-loading", "trust-balance-error", "client-accounting-enabled", "trust-insufficient", "saving", "saving-column", "error", "column-error", "stage-badge"])
                        ], 64))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("a", {
                        class: _normalizeClass(["d-flex align-center gap-1 text-decoration-none text-grey-darken-2 text-body-2", __props.downloadUrl ? 'cursor-pointer' : 'cursor-not-allowed opacity-60 pointer-events-none']),
                        href: __props.downloadUrl || undefined,
                        download: "settlement-statement.pdf",
                        "data-cy": "settlement-statement-link",
                        "aria-disabled": !__props.downloadUrl,
                        onClick: _cache[13] || (_cache[13] = ($event: any) => (__props.downloadUrl && _ctx.$emit('download')))
                      }, [
                        _createVNode(_component_v_icon, { size: "15" }, {
                          default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                            _createTextVNode("mdi-file-document-outline", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[25] || (_cache[25] = _createTextVNode(" Settlement Statement ", -1))
                      ], 10, _hoisted_11),
                      (__props.invoiceFinalized)
                        ? (_openBlock(), _createElementBlock("a", {
                            key: 0,
                            class: _normalizeClass(["d-flex align-center gap-1 text-decoration-none text-grey-darken-2 text-body-2", [
              __props.trustStatementLoading ? 'cursor-not-allowed opacity-60 pointer-events-none' : 'cursor-pointer',
            ]]),
                            "data-cy": "trust-statement-link",
                            "aria-disabled": __props.trustStatementLoading,
                            onClick: _cache[14] || (_cache[14] = _withModifiers(($event: any) => (!__props.trustStatementLoading && _ctx.$emit('download-trust-statement')), ["prevent"]))
                          }, [
                            _createVNode(_component_v_icon, { size: "15" }, {
                              default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                                _createTextVNode("mdi-file-document-outline", -1)
                              ]))]),
                              _: 1
                            }),
                            _createTextVNode(" " + _toDisplayString(__props.trustStatementLoading ? 'Downloading Trust Statement...' : 'Trust Statement') + " ", 1),
                            _createVNode(LlTooltip, { location: "top" }, {
                              activator: _withCtx(() => [
                                _createVNode(_component_v_icon, {
                                  size: "13",
                                  class: "cursor-pointer text-grey"
                                }, {
                                  default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                                    _createTextVNode("mdi-information-outline", -1)
                                  ]))]),
                                  _: 1
                                })
                              ]),
                              default: _withCtx(() => [
                                _cache[28] || (_cache[28] = _createElementVNode("div", { class: "max-w-[260px]" }, " A summary of all trust account activity associated with this settlement. ", -1))
                              ]),
                              _: 1
                            })
                          ], 10, _hoisted_12))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 1
                }))
          ], 64))
        : (__props.generating || __props.polling)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createVNode(_component_v_card_title, { class: "text-h6 font-weight-bold pb-0" }, {
                default: _withCtx(() => [
                  _createTextVNode("Step " + _toDisplayString(__props.stepNumber) + ": Generate Settlement Statement", 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_13, [
                    _createVNode(_component_v_progress_circular, {
                      indeterminate: "",
                      color: "success",
                      size: "48"
                    }),
                    _cache[29] || (_cache[29] = _createElementVNode("div", { class: "text-body-1 font-weight-medium" }, " Generating settlement statement… ", -1)),
                    _cache[30] || (_cache[30] = _createElementVNode("div", { class: "text-caption text-grey" }, " This usually takes a few seconds. Please wait. ", -1))
                  ])
                ]),
                _: 1
              })
            ], 64))
          : (__props.timedOut)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                _createVNode(_component_v_card_title, { class: "text-h6 font-weight-bold pb-0" }, {
                  default: _withCtx(() => [
                    _createTextVNode("Step " + _toDisplayString(__props.stepNumber) + ": Generate Settlement Statement", 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => [
                    _createVNode(AlertBar, {
                      class: "mb-4",
                      "data-cy": "timeout-alert"
                    }, {
                      default: _withCtx(() => [
                        _cache[32] || (_cache[32] = _createElementVNode("div", { class: "font-weight-bold mb-1" }, "Still generating…", -1)),
                        _cache[33] || (_cache[33] = _createElementVNode("p", { class: "mb-2 text-body-2" }, " The statement is taking longer than expected. The PDF may still be processing. ", -1)),
                        _createVNode(LlSecondaryButton, {
                          color: "warning",
                          loading: __props.saving,
                          "data-cy": "retry-polling-button",
                          onClick: _cache[15] || (_cache[15] = ($event: any) => (_ctx.$emit('retry-polling')))
                        }, {
                          default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                            _createTextVNode(" Check again ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["loading"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                _createVNode(_component_v_card_title, { class: "text-h6 font-weight-bold pb-0" }, {
                  default: _withCtx(() => [
                    _createTextVNode("Step " + _toDisplayString(__props.stepNumber) + ": Generate Settlement Statement", 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => [
                    (!__props.canGenerate)
                      ? (_openBlock(), _createBlock(AlertBar, {
                          key: 0,
                          class: "mb-4",
                          "data-cy": "cannot-generate-alert"
                        }, {
                          default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                            _createTextVNode(" All prior steps (1–5) must be completed before you can generate the settlement statement. ", -1)
                          ]))]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    _createVNode(AlertBar, {
                      type: "info",
                      class: "mb-4",
                      "data-cy": "generation-info-alert"
                    }, {
                      default: _withCtx(() => [...(_cache[35] || (_cache[35] = [
                        _createTextVNode(" Generating the statement will lock the settlement and produce a PDF. You can unlock and reset afterwards if changes are needed. ", -1)
                      ]))]),
                      _: 1
                    }),
                    (__props.previewGenerating || __props.previewPolling)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                          _createVNode(_component_v_progress_circular, {
                            indeterminate: "",
                            color: "blue",
                            size: "48"
                          }),
                          _cache[36] || (_cache[36] = _createElementVNode("div", { class: "text-body-1 font-weight-medium" }, "Generating statement preview…", -1)),
                          _cache[37] || (_cache[37] = _createElementVNode("div", { class: "text-caption text-grey" }, "This usually takes a few seconds.", -1))
                        ]))
                      : (__props.previewTimedOut)
                        ? (_openBlock(), _createBlock(AlertBar, {
                            key: 2,
                            class: "mb-4",
                            "data-cy": "preview-timeout-alert"
                          }, {
                            default: _withCtx(() => [
                              _cache[39] || (_cache[39] = _createElementVNode("div", { class: "font-weight-bold mb-1" }, "Preview taking longer than expected…", -1)),
                              _cache[40] || (_cache[40] = _createElementVNode("p", { class: "mb-2 text-body-2" }, "The preview PDF is still being generated.", -1)),
                              _createVNode(LlSecondaryButton, {
                                color: "warning",
                                "data-cy": "retry-preview-button",
                                onClick: _cache[16] || (_cache[16] = ($event: any) => (_ctx.$emit('retry-preview')))
                              }, {
                                default: _withCtx(() => [...(_cache[38] || (_cache[38] = [
                                  _createTextVNode(" Try again ", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }))
                        : (__props.previewUrl)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                              _createVNode(_unref(VuePdfEmbed), { source: __props.previewUrl }, null, 8, ["source"])
                            ]))
                          : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_16, [
                      _createVNode(LlSecondaryButton, {
                        onClick: _cache[17] || (_cache[17] = ($event: any) => (_ctx.$emit('go-back'))),
                        "data-cy": "back-button"
                      }, {
                        default: _withCtx(() => [...(_cache[41] || (_cache[41] = [
                          _createTextVNode(" ← Back ", -1)
                        ]))]),
                        _: 1
                      }),
                      (__props.canGenerate)
                        ? (_openBlock(), _createBlock(LlSecondaryButton, {
                            key: 0,
                            color: "primary",
                            loading: __props.saving,
                            "data-cy": "generate-button",
                            onClick: _cache[18] || (_cache[18] = ($event: any) => (_ctx.$emit('generate')))
                          }, {
                            default: _withCtx(() => [...(_cache[42] || (_cache[42] = [
                              _createTextVNode(" Generate Statement ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["loading"]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 1
                })
              ], 64))
    ]),
    _: 1
  }))
}
}

})